import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Typography } from "@pondigitalsolutions/pon-component-library";
import { Pill } from "./Pill";
import { highlightText } from "@utils/highlightText";
import { PeopleFinderPerson } from "@hooks/peopleFinder/usePeopleFinder";
import { useTranslation } from "react-i18next";
import { Check, Copy03 } from "untitledui-js/react";
import { CopyLabel } from "./CopyLabel";

interface PeopleFinderCardProps {
  person: PeopleFinderPerson;
  searchTerm?: string;
  onClick: () => void;
}

export const PeopleFinderCard = ({
  person,
  searchTerm,
  onClick,
}: PeopleFinderCardProps) => {
  const { t } = useTranslation();

  const [nameCopied, setNameCopied] = useState(false);

  useEffect(() => {
    if (nameCopied) {
      const timer = setTimeout(() => setNameCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [nameCopied]);

  return (
    <div
      role="button"
      tabIndex={0}
      className={twMerge(
        "group flex justify-between rounded-sm border border-tertiary-50 bg-tertiary-50 px-2 py-3 lg:items-center lg:gap-4 lg:px-3 lg:py-2 dark:border-white/10 dark:bg-white/10",
        "hover:cursor-pointer hover:border-tertiary-100 hover:bg-tertiary-100 dark:hover:border-white/20 dark:hover:bg-white/20"
      )}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={t("PeopleFinder.viewDetails", {
        name: `${person.firstName} ${person.lastName}`,
      })}
    >
      <div className="flex w-full flex-col justify-between gap-2 lg:flex-row lg:items-center lg:gap-3">
        <div className="flex items-center gap-3">
          <div className="flex aspect-square h-10 w-10 items-center justify-center rounded-full bg-tertiary-100 lg:h-14 lg:w-14 dark:bg-white/20">
            <Typography
              size="lg"
              className="leading-[130%] font-bold text-tertiary-300 dark:text-tertiary-200"
            >
              {person.firstName.charAt(0)}
            </Typography>
          </div>

          <div>
            <div className="group/name mr-5.5 flex h-6 w-fit items-center hover:mr-0 hover:gap-1">
              <Typography className="leading-none font-medium text-secondary-700 lg:text-lg dark:text-white">
                {searchTerm ? (
                  <>
                    {highlightText(
                      `${person.firstName} ${person.lastName}`,
                      searchTerm
                    )}
                  </>
                ) : (
                  `${person.firstName} ${person.lastName}`
                )}
              </Typography>
              <button
                className={twMerge(
                  "hidden h-6 w-6 cursor-copy items-center justify-center rounded-full border group-hover/name:flex",
                  nameCopied
                    ? "border-accent_two-500 bg-accent_two-500 dark:bg-transparent"
                    : "border-tertiary-100 bg-tertiary-50 hover:border-tertiary-200 hover:bg-tertiary-200 dark:border-white/10 dark:bg-secondary-900 [&>svg]:stroke-secondary-700 hover:[&>svg]:stroke-secondary-800 [&>svg]:dark:stroke-white"
                )}
                onClick={(e) => {
                  // Prevent to redirect to the detail page.
                  e.preventDefault();
                  e.stopPropagation();
                  void navigator.clipboard.writeText(
                    `${person.firstName} ${person.lastName}`
                  );
                  setNameCopied(true);
                }}
                aria-label={t("PeopleFinder.copyName")}
              >
                {nameCopied ? (
                  <Check className="h-3.5 w-3.5 stroke-white dark:stroke-accent_two-300" />
                ) : (
                  <Copy03 className="h-3.5 w-3.5" />
                )}
              </button>
            </div>

            {(person.opcoFunctionTitle || person.opcoLevel) && (
              <div className="items-center gap-1.5 lg:flex">
                <Typography
                  size="xs"
                  className="leading-[125%] font-normal text-tertiary-700 lg:text-sm dark:text-tertiary-300"
                >
                  {searchTerm ? (
                    <>
                      {person.opcoFunctionTitle && (
                        <>
                          {highlightText(person.opcoFunctionTitle, searchTerm)}{" "}
                          {t("PeopleFinder.at")}{" "}
                        </>
                      )}
                      {highlightText(person.opcoLevel, searchTerm)}
                    </>
                  ) : (
                    <>
                      {person.opcoFunctionTitle && (
                        <>
                          {person.opcoFunctionTitle} {t("PeopleFinder.at")}{" "}
                        </>
                      )}
                      {person.opcoLevel}
                    </>
                  )}
                </Typography>
              </div>
            )}
          </div>
        </div>

        {person.opcoEmailAccount || person.ponMobilePhone ? (
          <div className="hidden flex-wrap items-center justify-end gap-3 lg:flex">
            {person.opcoEmailAccount && (
              <CopyLabel textCopy={person.opcoEmailAccount}>
                {person.opcoEmailAccount}
              </CopyLabel>
            )}
            {person.ponMobilePhone && (
              <CopyLabel textCopy={person.ponMobilePhone}>
                {person.ponMobilePhone}
              </CopyLabel>
            )}
          </div>
        ) : null}

        {/* Mobile view */}
        {person.opcoEmailAccount || person.ponMobilePhone ? (
          <div className="flex flex-wrap items-center gap-1 lg:hidden">
            {person.opcoEmailAccount && (
              <Pill
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `mailto:${person.opcoEmailAccount}`;
                }}
              >
                {person.opcoEmailAccount}
              </Pill>
            )}
            {person.ponMobilePhone && (
              <Pill
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `tel:${person.ponMobilePhone}`;
                }}
              >
                {person.ponMobilePhone}
              </Pill>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
};
